import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabase';
import type { BrandRow } from '../../../../lib/supabase';

interface Props {
  brand: BrandRow | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  name: string;
  description: string;
  logo_url: string;
  sort_order: string;
  is_active: boolean;
}

const defaultForm: FormState = {
  name: '',
  description: '',
  logo_url: '',
  sort_order: '0',
  is_active: true,
};

export default function BrandFormModal({ brand, onClose, onSaved }: Props) {
  const [form, setForm] = useState<FormState>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (brand) {
      setForm({
        name: brand.name,
        description: brand.description,
        logo_url: brand.logo_url,
        sort_order: String(brand.sort_order),
        is_active: brand.is_active,
      });
    } else {
      setForm(defaultForm);
    }
  }, [brand]);

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('請填寫品牌名稱'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      logo_url: form.logo_url.trim(),
      sort_order: parseInt(form.sort_order, 10) || 0,
      is_active: form.is_active,
      updated_at: new Date().toISOString(),
    };
    let err;
    if (brand) {
      ({ error: err } = await supabase.from('agency_brands').update(payload).eq('id', brand.id));
    } else {
      ({ error: err } = await supabase.from('agency_brands').insert(payload));
    }
    setSaving(false);
    if (err) { setError(err.message); return; }
    onSaved();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-[#1a2535]">
            {brand ? '編輯代理品牌' : '新增代理品牌'}
          </h3>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Logo Preview */}
          {form.logo_url && (
            <div className="flex items-center justify-center h-24 border border-gray-100 bg-gray-50 rounded">
              <img
                src={form.logo_url}
                alt="品牌 Logo 預覽"
                className="max-h-20 max-w-full object-contain"
              />
            </div>
          )}

          {/* Logo URL */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Logo 圖片網址</label>
            <input
              type="url"
              value={form.logo_url}
              onChange={e => handleChange('logo_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 text-sm border border-gray-200 focus:outline-none focus:border-[#1a73b5]"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              品牌名稱 <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="例如：Appleton"
              className="w-full px-3 py-2 text-sm border border-gray-200 focus:outline-none focus:border-[#1a73b5]"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">品牌簡介</label>
            <textarea
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="簡短描述品牌特色與產品範疇..."
              className="w-full px-3 py-2 text-sm border border-gray-200 focus:outline-none focus:border-[#1a73b5] resize-none"
            />
            <p className="text-right text-[10px] text-gray-400 mt-0.5">{form.description.length}/500</p>
          </div>

          {/* Sort Order & Status */}
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">排序</label>
              <input
                type="number"
                value={form.sort_order}
                onChange={e => handleChange('sort_order', e.target.value)}
                min="0"
                className="w-full px-3 py-2 text-sm border border-gray-200 focus:outline-none focus:border-[#1a73b5]"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-600 mb-1">顯示狀態</label>
              <select
                value={form.is_active ? 'true' : 'false'}
                onChange={e => handleChange('is_active', e.target.value === 'true')}
                className="w-full px-3 py-2 text-sm border border-gray-200 focus:outline-none focus:border-[#1a73b5] bg-white cursor-pointer"
              >
                <option value="true">顯示</option>
                <option value="false">隱藏</option>
              </select>
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-500 bg-red-50 px-3 py-2">{error}</p>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 text-sm border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2 text-sm bg-[#1a73b5] text-white hover:bg-[#1558a0] cursor-pointer disabled:opacity-60 whitespace-nowrap"
            >
              {saving ? '儲存中...' : (brand ? '更新品牌' : '新增品牌')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
